import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PageShellProps {
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  hero?: ReactNode;
  flush?: boolean;
  maxWidth?: 'full' | '4xl' | '6xl';
  className?: string;
  children: ReactNode;
}

const maxWidthMap: Record<NonNullable<PageShellProps['maxWidth']>, string> = {
  full: 'w-full',
  '4xl': 'max-w-4xl',
  '6xl': 'max-w-6xl',
};

/**
 * Wrapper padrão de páginas do clearance. Espaçamento generoso,
 * tipografia confortável e estrutura topbar → hero → conteúdo.
 *
 * Default: maxWidth="6xl", padding mais generoso que o manager.
 */
export function PageShell({
  breadcrumb,
  actions,
  title,
  description,
  hero,
  flush,
  maxWidth = '6xl',
  className,
  children,
}: PageShellProps) {
  const hasTopbar = Boolean(breadcrumb || actions);
  const hasHeroContent = Boolean(hero || title || description);

  return (
    <div
      className={cn(
        'flex flex-1 flex-col gap-8 px-6 md:px-10 pt-8',
        flush ? 'pb-0' : 'pb-12',
        className
      )}
    >
      <div className={cn('mx-auto w-full flex flex-col gap-8', maxWidthMap[maxWidth])}>
        {hasTopbar && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex items-center">{breadcrumb}</div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        )}

        {hasHeroContent && (
          <header className="space-y-3">
            {hero}
            {title && !hero && (
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
            )}
            {description && !hero && (
              <p className="text-base text-muted-foreground max-w-3xl leading-relaxed">
                {description}
              </p>
            )}
          </header>
        )}

        {children}
      </div>
    </div>
  );
}
